import Cookies from 'js-cookie';
import { useQueryClient } from 'react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { Text } from 'components/primitives/typography';
import ArchiveIntroductionModal from 'components/modals/ArchiveIntroductionModal';
import useModal from 'components/remodal/v2/useModal';
import { useUserDetails } from 'auth/mutations/userHooks';
import useLazySnackbar from 'components/banners/useLazySnackbar';
import { activeConversationIdAtom, activeTabNameAtom } from '../store';
import useReadArchiveMessageMutation from './useReadArchiveMessageMutation';
import { TabNames } from '../constants';

export default function useToggleMessageArchived(conversation) {
  const { enqueueSnackbar, closeSnackbar } = useLazySnackbar();
  const queryClient = useQueryClient();
  const { userId } = useUserDetails();

  const activeConversationId = useAtomValue(activeConversationIdAtom);
  const activeTabName = useAtomValue(activeTabNameAtom);
  const setActiveConversationId = useSetAtom(activeConversationIdAtom);
  const setActiveTabName = useSetAtom(activeTabNameAtom);

  const { modal, openModal } = useModal(
    <ArchiveIntroductionModal onContinue={() => archiveMessageMutation.mutate({ archive: !conversation.archived })} />,
    {
      size: 'medium',
      trackingName: 'archive_introduction_modal',
    }
  );

  const archiveMessageMutation = useReadArchiveMessageMutation(activeConversationId, conversation.booking.id, {
    onSuccess: async (_, { archive }) => {
      const snackbarKey = await enqueueSnackbar(
        archive ? 'Conversation has been archived. ' : 'Conversation has been unarchived. ',
        {
          autoHideDuration: 4000,
          variant: 'success',
          onClick: () => {},
          action: (
            <Text
              as="span"
              cursor="pointer"
              textDecoration="underline"
              onClick={() => {
                archiveMessageMutation.mutate({ archive: !archive });
                closeSnackbar(snackbarKey);
              }}
            >
              Undo
            </Text>
          ),
        }
      );

      // set next or prev conversation if exists
      const conversationsTabData = queryClient.getQueryData([
        'conversationsTab',
        activeTabName,
        userId,
        !archive,
      ]) as any;
      const allConversations = conversationsTabData?.pages?.map(({ data }) => data)?.flat();
      if (allConversations) {
        const currentConversationIndex = allConversations.findIndex(
          ({ conversation_id }) => conversation_id === activeConversationId
        );
        const nextConversationId = allConversations[currentConversationIndex + 1]?.conversation_id;
        const prevConversationId = allConversations[currentConversationIndex - 1]?.conversation_id;
        if (nextConversationId) {
          setActiveConversationId(nextConversationId);
        } else if (prevConversationId) {
          setActiveConversationId(prevConversationId);
        } else {
          setActiveTabName(TabNames.renter);
        }
      }
    },
  });

  const toggleMessageArchived = () => {
    const hasUsed = !!Cookies.get('archive_feature_used');
    if (!hasUsed && !conversation.archived) {
      openModal();
    } else {
      archiveMessageMutation.mutate({ archive: !conversation.archived });
    }
    Cookies.set('archive_feature_used', 'true');
  };

  return { toggleMessageArchived, archiveMessageMutation, archiveIntroductionModal: modal };
}
